import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import {
  Wallet, AlertTriangle, TrendingUp, TrendingDown, Percent, CheckCircle,
  Plus, Loader2, Receipt, LayoutList, PieChart as PieIcon,
  MessageCircle, Send, ChevronRight, ChevronDown, ArrowLeft, Users,
} from 'lucide-react';
import { Toaster } from '../components/ui/sonner';
import { buildReminder, buildClientReminder } from '../lib/reminder';

const fmt = (n) => `${Number(n || 0).toLocaleString('ru-RU')} ₸`;

const METHOD_LABELS_MINI = {
  transfer: 'Перевод', cash: 'Наличные', kaspi: 'Kaspi', card: 'Карта',
};

// Open a reminder link using Telegram WebApp helpers when available.
function openReminder(card, channel) {
  const { waUrl, tgUrl } = buildReminder(card);
  const tg = window.Telegram?.WebApp;
  if (channel === 'whatsapp') {
    if (!waUrl) { toast.error('У клиента не указан телефон'); return; }
    if (tg?.openLink) tg.openLink(waUrl); else window.open(waUrl, '_blank', 'noopener');
  } else {
    if (tg?.openTelegramLink) tg.openTelegramLink(tgUrl); else window.open(tgUrl, '_blank', 'noopener');
  }
}

// Same idea but for a client-level card (aggregate reminder).
function openClientReminder(client, channel) {
  const { waUrl, tgUrl } = buildClientReminder(client);
  const tg = window.Telegram?.WebApp;
  if (channel === 'whatsapp') {
    if (!waUrl) { toast.error('У клиента не указан телефон'); return; }
    if (tg?.openLink) tg.openLink(waUrl); else window.open(waUrl, '_blank', 'noopener');
  } else {
    if (tg?.openTelegramLink) tg.openTelegramLink(tgUrl); else window.open(tgUrl, '_blank', 'noopener');
  }
}

// initData from Telegram WebView. Telegram passes it either via window.Telegram.WebApp
// (SDK) or the URL hash (#tgWebAppData=...). We also accept ?initData=... for preview testing.
const INITIAL_HASH = typeof window !== 'undefined' ? window.location.hash : '';
function hashInitData() {
  try {
    for (const h of [INITIAL_HASH, typeof window !== 'undefined' ? window.location.hash : '']) {
      const v = new URLSearchParams((h || '').replace(/^#/, '')).get('tgWebAppData');
      if (v) return v;
    }
  } catch (e) { /* noop */ }
  return '';
}
function queryInitData() {
  try { return new URLSearchParams(window.location.search).get('initData') || ''; } catch (e) { return ''; }
}

function FinanceApp() {
  const [initData, setInitData] = useState('');
  const [booting, setBooting] = useState(true);
  const [me, setMe] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [tab, setTab] = useState('summary');
  const client = axios.create({ baseURL: API, headers: { 'X-Telegram-Init-Data': initData } });

  // Resolve initData: SDK object → URL hash → ?initData query. Load Telegram SDK if needed.
  useEffect(() => {
    let cancelled = false;
    const q = queryInitData();
    if (q) { setInitData(q); setBooting(false); return; }
    const tryBoot = (attempt = 0) => {
      if (cancelled) return;
      const tg = window.Telegram?.WebApp;
      const data = (tg && tg.initData) || hashInitData();
      if (tg) { try { tg.expand(); tg.ready(); } catch (e) {} }
      if (data) { setInitData(data); setBooting(false); return; }
      if (attempt < 12) { setTimeout(() => tryBoot(attempt + 1), 200); return; }
      setBooting(false);
    };
    if (window.Telegram?.WebApp || hashInitData()) { tryBoot(); return; }
    const s = document.createElement('script');
    s.src = 'https://telegram.org/js/telegram-web-app.js';
    s.onload = () => tryBoot();
    s.onerror = () => tryBoot();
    document.head.appendChild(s);
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (booting) return;
    if (!initData) { setAuthError('Откройте приложение внутри Telegram.'); return; }
    client.get('/finance-app/me')
      .then(r => setMe(r.data))
      .catch(err => setAuthError(err.response?.status === 403 ? 'Нет доступа. Запросите ссылку у администратора.' : 'Откройте приложение внутри Telegram.'));
    // eslint-disable-next-line
  }, [booting, initData]);

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 text-center" data-testid="finapp-auth-error">
        <div>
          <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">Финансы Ainanan</p>
          <p className="text-sm text-gray-500 mt-2">{authError}</p>
        </div>
      </div>
    );
  }
  if (!me) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="finance-app">
      <Toaster position="top-center" />
      <header className="bg-emerald-700 text-white px-4 py-3 sticky top-0 z-10">
        <div className="text-sm opacity-80">Финансы Ainanan</div>
        <div className="font-semibold">{me.name}</div>
      </header>

      <div className="p-3">
        {tab === 'summary' && <SummaryTab client={client} />}
        {tab === 'receivables' && <ReceivablesTab client={client} />}
        {tab === 'expenses' && <ExpensesTab client={client} />}
        {tab === 'pnl' && <PnlTab client={client} />}
      </div>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t flex justify-around py-1.5 z-20">
        {[
          ['summary', 'Сводка', Wallet],
          ['receivables', 'Дебиторка', LayoutList],
          ['expenses', 'Расходы', Receipt],
          ['pnl', 'P&L', PieIcon],
        ].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 text-[11px] ${tab === key ? 'text-emerald-700' : 'text-gray-400'}`}
            data-testid={`finapp-nav-${key}`}>
            <Icon className="w-5 h-5" /> {label}
          </button>
        ))}
      </nav>
    </div>
  );
}

function Stat({ icon: Icon, tint, label, value }) {
  const tints = { emerald: 'bg-emerald-50 text-emerald-700', red: 'bg-red-50 text-red-700', blue: 'bg-blue-50 text-blue-700' };
  return (
    <div className="bg-white rounded-xl p-3 flex items-center gap-3 border">
      <div className={`p-2 rounded-lg ${tints[tint]}`}><Icon className="w-5 h-5" /></div>
      <div><div className="text-[11px] text-gray-500">{label}</div><div className="text-lg font-bold">{value}</div></div>
    </div>
  );
}

function SummaryTab({ client }) {
  const [d, setD] = useState(null);
  useEffect(() => { client.get('/finance-app/summary').then(r => setD(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-emerald-600 mx-auto mt-8" />;
  return (
    <div className="space-y-2" data-testid="finapp-summary">
      <div className="text-xs text-gray-500 font-medium px-1">Дебиторка</div>
      <div className="grid grid-cols-1 gap-2">
        <Stat icon={Wallet} tint="emerald" label="Всего к оплате" value={fmt(d.receivables.total)} />
        <div className="grid grid-cols-2 gap-2">
          <Stat icon={AlertTriangle} tint="red" label="Просрочено" value={fmt(d.receivables.overdue)} />
          <Stat icon={TrendingUp} tint="blue" label="Обещали сегодня" value={fmt(d.receivables.promised_today)} />
        </div>
      </div>
      <div className="text-xs text-gray-500 font-medium px-1 pt-2">P&L (тек. месяц)</div>
      <div className="grid grid-cols-2 gap-2">
        <Stat icon={TrendingUp} tint="emerald" label="Выручка" value={fmt(d.pnl.revenue)} />
        <Stat icon={TrendingDown} tint="red" label="Расходы" value={fmt(d.pnl.expenses)} />
        <Stat icon={Wallet} tint={d.pnl.profit >= 0 ? 'emerald' : 'red'} label="Прибыль" value={fmt(d.pnl.profit)} />
        <Stat icon={Percent} tint="blue" label="Маржа" value={`${d.pnl.margin}%`} />
      </div>
    </div>
  );
}

const COLS = [['overdue', '🔴 Просрочено'], ['promised', '🔵 Обещали'], ['waiting', '🟡 Ожидает']];

// Wrapper: Receivables tab now has a segmented control between the classic
// order-level board and a new client-level AR-aging view.
function ReceivablesTab({ client }) {
  const [seg, setSeg] = useState('orders');  // 'orders' | 'clients'
  return (
    <div className="space-y-2" data-testid="finapp-receivables">
      <div className="flex rounded-lg border overflow-hidden text-xs">
        <button onClick={() => setSeg('orders')}
          className={`flex-1 py-2 flex items-center justify-center gap-1 ${seg === 'orders' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'}`}
          data-testid="finapp-seg-orders">
          <LayoutList className="w-3.5 h-3.5" /> Заказы
        </button>
        <button onClick={() => setSeg('clients')}
          className={`flex-1 py-2 flex items-center justify-center gap-1 ${seg === 'clients' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'}`}
          data-testid="finapp-seg-clients">
          <Users className="w-3.5 h-3.5" /> Клиенты
        </button>
      </div>
      {seg === 'orders' ? <OrdersBoardTab client={client} /> : <ClientsAgingTab client={client} />}
    </div>
  );
}

function OrdersBoardTab({ client }) {
  const [board, setBoard] = useState(null);
  const load = useCallback(() => { client.get('/finance-app/receivables').then(r => setBoard(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); }, [load]);
  const pay = async (id) => {
    try { await client.post(`/finance-app/receivables/${id}/pay`); toast.success('Оплачено'); load(); }
    catch { toast.error('Ошибка'); }
  };
  if (!board) return <Loader2 className="w-5 h-5 animate-spin text-emerald-600 mx-auto mt-8" />;
  return (
    <div className="space-y-3" data-testid="finapp-orders-board">
      {COLS.map(([key, label]) => (
        <div key={key}>
          <div className="text-xs font-semibold text-gray-600 mb-1 px-1">{label} · {board.counts[key]}</div>
          <div className="space-y-2">
            {board.columns[key].slice(0, 30).map(c => (
              <div key={c.id} className="bg-white rounded-lg border p-3" data-testid={`finapp-card-${c.order_number}`}>
                <div className="flex justify-between">
                  <span className="font-medium text-sm">{c.customer_name}</span>
                  <span className="font-bold text-sm">{fmt(c.amount)}</span>
                </div>
                <div className="text-[11px] text-gray-500">#{c.order_number}{c.promised_payment_date && ` · обещал ${c.promised_payment_date}`} · {c.days_outstanding}д</div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button onClick={() => openReminder(c, 'whatsapp')} disabled={!c.customer_phone}
                    className="text-xs text-green-700 border border-green-300 rounded py-1.5 flex items-center justify-center gap-1 disabled:opacity-40"
                    data-testid={`finapp-remind-wa-${c.order_number}`}>
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </button>
                  <button onClick={() => openReminder(c, 'telegram')}
                    className="text-xs text-blue-700 border border-blue-300 rounded py-1.5 flex items-center justify-center gap-1"
                    data-testid={`finapp-remind-tg-${c.order_number}`}>
                    <Send className="w-3.5 h-3.5" /> Telegram
                  </button>
                </div>
                <button onClick={() => pay(c.id)} className="mt-2 w-full text-xs text-green-700 border border-green-300 rounded py-1.5 flex items-center justify-center gap-1" data-testid={`finapp-pay-${c.order_number}`}>
                  <CheckCircle className="w-3.5 h-3.5" /> Оплачено
                </button>
              </div>
            ))}
            {board.columns[key].length === 0 && <div className="text-center text-xs text-gray-400 py-2">пусто</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// AR-aging accordion: buckets clients by the age of their oldest unpaid order.
// On a phone the classic 4-column kanban doesn't fit, so we use a vertical
// accordion (Критично open by default — that's what needs attention today).
const BUCKETS = [
  { id: 'critical',  label: '🔴 Критично',   sub: '60+ дней',   maxDays: Infinity, defaultOpen: true },
  { id: 'overdue',   label: '🟠 Просрочка',  sub: '31–60 дней', maxDays: 60 },
  { id: 'attention', label: '🟡 Внимание',   sub: '8–30 дней',  maxDays: 30 },
  { id: 'fresh',     label: '🟢 Свежий',     sub: '0–7 дней',   maxDays: 7 },
];

function ClientsAgingTab({ client }) {
  const [data, setData] = useState(null);
  const [role, setRole] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set(BUCKETS.filter(b => b.defaultOpen).map(b => b.id)));
  const [drill, setDrill] = useState(null);  // currently selected client (drill-down view)
  const [balance, setBalance] = useState(null);  // акт сверки for the drilled ИП (B2B only)
  const [payForm, setPayForm] = useState(null);  // {amount, payment_date, method, note} or null (dialog closed)
  const [paySaving, setPaySaving] = useState(false);

  // Remaining balance after ledger payments (акт сверки); falls back to raw debt.
  const bal = (c) => (c && c.balance != null ? c.balance : (c ? c.total_owed : 0));

  const load = useCallback(async () => {
    const params = role === 'all' ? '' : `?role=${role}`;
    try {
      const r = await client.get(`/finance-app/receivables/by-client${params}`);
      setData(r.data);
      // Keep the drilled client in sync with fresh data (or close it once settled).
      setDrill(prev => {
        if (!prev) return prev;
        const fresh = r.data.clients.find(c => prev.bin_iin ? c.bin_iin === prev.bin_iin : c.user_id === prev.user_id);
        return fresh || null;
      });
    } catch { toast.error('Не удалось загрузить'); }
  }, [client, role]);
  useEffect(() => { load(); }, [load]);

  // Load акт сверки for the drilled client:
  //  • B2B ИП → entity-balance endpoint (accrued incl. paid orders).
  //  • B2C / individual → derive from the already-loaded client row.
  const loadBalance = useCallback(async () => {
    if (!drill) { setBalance(null); return; }
    if (drill.bin_iin) {
      try {
        const r = await client.get(`/finance-app/receivables/entity-balance/${drill.bin_iin}`);
        setBalance(r.data);
      } catch { setBalance(null); }
    } else {
      setBalance({
        accrued: drill.total_owed,
        paid_total: drill.paid_ledger_total || 0,
        balance: drill.balance != null ? drill.balance : drill.total_owed,
        payments: drill.payments || [],
        by_user: true,
      });
    }
  }, [client, drill]);
  useEffect(() => { loadBalance(); }, [loadBalance]);

  const toggle = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const pay = async (orderId, orderNum) => {
    try {
      await client.post(`/finance-app/receivables/${orderId}/pay`);
      toast.success(`#${orderNum} оплачен`);
      load();
      // Also refresh drill-down: remove the paid order from the modal
      if (drill) {
        const remaining = drill.orders.filter(o => o.id !== orderId);
        if (remaining.length === 0) setDrill(null);
        else setDrill({ ...drill, orders: remaining, orders_count: remaining.length,
          total_owed: remaining.reduce((s, o) => s + o.amount, 0) });
      }
    } catch { toast.error('Ошибка'); }
  };

  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-emerald-600 mx-auto mt-8" />;

  // Bucket clients into the 4 aging groups (sorted by BUCKETS order ⇒ Критично first)
  const bucketed = BUCKETS.map(b => ({ ...b, clients: [] }));
  data.clients.forEach(c => {
    // Match against buckets in REVERSE age order (fresh → critical) — first bucket whose maxDays fits.
    // We wrote them from Критично to Свежий, so reverse-iterate to find the smallest fitting one.
    for (let i = bucketed.length - 1; i >= 0; i--) {
      if (c.oldest_days <= bucketed[i].maxDays) { bucketed[i].clients.push(c); break; }
    }
  });
  bucketed.forEach(b => b.clients.sort((x, y) => bal(y) - bal(x)));

  // Drill-down: show a single client's orders instead of the accordion
  if (drill) {
    return (
      <div className="space-y-2" data-testid="finapp-client-drill">
        <button onClick={() => setDrill(null)}
          className="flex items-center gap-1 text-xs text-emerald-700 py-1"
          data-testid="finapp-drill-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Назад к клиентам
        </button>
        <div className="bg-white rounded-lg border p-3">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-semibold text-sm">{drill.customer_name}</div>
              <div className="text-[11px] text-gray-500">
                {drill.customer_type} · {drill.orders_count} заказов
                {drill.customer_email && ` · ${drill.customer_email}`}
              </div>
            </div>
            <div className={`text-lg font-bold whitespace-nowrap ${bal(drill) <= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(bal(drill))}</div>
          </div>
          {/* Balance breakdown (акт сверки) — B2B ИП and B2C */}
          {balance && (
            <div className="mt-2 grid grid-cols-3 gap-2 text-[10px] bg-gray-50 rounded p-2">
              <div>
                <div className="text-gray-500">{balance.by_user ? 'Долг' : 'Начислено'}</div>
                <div className="font-semibold text-gray-800">{fmt(balance.accrued)}</div>
              </div>
              <div>
                <div className="text-gray-500">Оплачено</div>
                <div className="font-semibold text-blue-700">{fmt(balance.paid_total)}</div>
              </div>
              <div>
                <div className="text-gray-500">Остаток</div>
                <div className={`font-bold ${balance.balance <= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {fmt(balance.balance)}
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 mt-2">
            <button onClick={() => openClientReminder(drill, 'whatsapp')} disabled={!drill.customer_phone}
              className="text-xs text-green-700 border border-green-300 rounded py-1.5 flex items-center justify-center gap-1 disabled:opacity-40"
              data-testid="finapp-drill-wa">
              <MessageCircle className="w-3.5 h-3.5" /> WA
            </button>
            <button onClick={() => openClientReminder(drill, 'telegram')}
              className="text-xs text-blue-700 border border-blue-300 rounded py-1.5 flex items-center justify-center gap-1"
              data-testid="finapp-drill-tg">
              <Send className="w-3.5 h-3.5" /> TG
            </button>
            {(drill.bin_iin || drill.user_id) && (
              <button onClick={() => setPayForm({ amount: '', payment_date: new Date().toISOString().slice(0,10), method: 'transfer', note: '' })}
                className="text-xs text-emerald-800 bg-emerald-100 border border-emerald-300 rounded py-1.5 flex items-center justify-center gap-1 font-semibold"
                data-testid="finapp-drill-record-payment">
                <Wallet className="w-3.5 h-3.5" /> Оплата
              </button>
            )}
          </div>
        </div>

        {/* Payments history (акт сверки) */}
        {balance && balance.payments && balance.payments.length > 0 && (
          <div className="bg-white rounded-lg border p-2" data-testid="finapp-drill-payments">
            <div className="text-xs font-semibold text-blue-900 mb-1">💰 История оплат ({balance.payments.length})</div>
            <div className="space-y-1">
              {balance.payments.map(p => (
                <div key={p.id} className="flex items-center justify-between border-t pt-1 text-[11px]" data-testid={`finapp-drill-pay-${p.id}`}>
                  <div>
                    <div className="font-semibold text-blue-800">{fmt(p.amount)}</div>
                    <div className="text-gray-500">{p.payment_date} · {METHOD_LABELS_MINI[p.method] || p.method}{p.note && ` · ${p.note}`}</div>
                  </div>
                  <button onClick={async () => {
                    if (!window.confirm('Удалить эту оплату?')) return;
                    try {
                      const r = await client.delete(`/finance-app/client-payments/${p.id}`);
                      const msg = r.data.rolled_back_orders ? `Удалено, ${r.data.rolled_back_orders} заказов возвращено` : 'Удалено';
                      toast.success(msg);
                      loadBalance(); load();
                    } catch { toast.error('Ошибка'); }
                  }}
                    className="text-[10px] text-red-600" data-testid={`finapp-drill-pay-del-${p.id}`}>×</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment recording dialog */}
        {payForm && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-3" onClick={() => setPayForm(null)}>
            <div className="bg-white rounded-lg w-full max-w-sm p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="finapp-pay-dialog">
              <div className="font-semibold text-sm">Записать оплату</div>
              <div className="text-[11px] text-gray-500">
                {drill.customer_name}{drill.bin_iin ? ` · БИН ${drill.bin_iin}` : (drill.customer_email ? ` · ${drill.customer_email}` : '')}
              </div>
              {balance && (
                <div className="text-[11px]">
                  Остаток: <b className="text-red-700">{fmt(balance.balance)}</b>
                </div>
              )}
              <div>
                <label className="text-[11px] text-gray-500 block mb-1">Сумма, ₸</label>
                <input type="number" min="1" value={payForm.amount}
                  onChange={(e) => setPayForm({...payForm, amount: e.target.value})}
                  className="w-full border rounded px-2 py-2 text-sm"
                  placeholder="5000000" autoFocus data-testid="finapp-pay-amount" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 block mb-1">Дата</label>
                <input type="date" value={payForm.payment_date}
                  onChange={(e) => setPayForm({...payForm, payment_date: e.target.value})}
                  className="w-full border rounded px-2 py-2 text-sm"
                  data-testid="finapp-pay-date" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 block mb-1">Способ</label>
                <select value={payForm.method}
                  onChange={(e) => setPayForm({...payForm, method: e.target.value})}
                  className="w-full border rounded px-2 py-2 text-sm"
                  data-testid="finapp-pay-method">
                  <option value="transfer">Банковский перевод</option>
                  <option value="cash">Наличные</option>
                  <option value="kaspi">Kaspi</option>
                  <option value="card">Карта</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] text-gray-500 block mb-1">Заметка</label>
                <input type="text" value={payForm.note}
                  onChange={(e) => setPayForm({...payForm, note: e.target.value})}
                  className="w-full border rounded px-2 py-2 text-sm"
                  placeholder="Напр. аванс за июль"
                  data-testid="finapp-pay-note" />
              </div>
              {payForm.amount && balance && balance.balance > 0 && (
                <div className="text-[11px] bg-gray-50 rounded p-2">
                  После оплаты: <b>{fmt(balance.balance - Number(payForm.amount))}</b>
                  {(balance.balance - Number(payForm.amount)) <= 0 && (
                    <div className="text-emerald-700 mt-1">⚠️ Все неоплаченные заказы будут автозакрыты</div>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setPayForm(null)}
                  className="flex-1 border rounded py-2 text-sm text-gray-600"
                  data-testid="finapp-pay-cancel">Отмена</button>
                <button
                  disabled={paySaving || !payForm.amount || Number(payForm.amount) <= 0}
                  onClick={async () => {
                    setPaySaving(true);
                    try {
                      const r = await client.post('/finance-app/client-payments', {
                        ...(drill.bin_iin ? { bin_iin: drill.bin_iin } : { user_id: drill.user_id }),
                        amount: Number(payForm.amount),
                        payment_date: payForm.payment_date,
                        method: payForm.method,
                        note: payForm.note || null,
                      });
                      const closed = r.data.auto_closed_count || 0;
                      toast.success(closed ? `Записано, ${closed} заказов закрыто` : 'Записано');
                      setPayForm(null);
                      loadBalance(); load();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Ошибка');
                    } finally { setPaySaving(false); }
                  }}
                  className="flex-1 bg-emerald-600 text-white rounded py-2 text-sm disabled:opacity-50"
                  data-testid="finapp-pay-save">
                  {paySaving ? '…' : 'Записать'}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-2">
          {drill.orders.map(o => (
            <div key={o.id} className={`bg-white rounded-lg border p-2.5 ${o.overdue ? 'border-red-200 bg-red-50/30' : ''}`}
              data-testid={`finapp-drill-order-${o.order_number}`}>
              <div className="flex justify-between">
                <span className="text-sm font-mono">#{o.order_number}</span>
                <span className="text-sm font-semibold">{fmt(o.amount)}</span>
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                создан {o.created_at ? new Date(o.created_at).toLocaleDateString('ru-RU') : '—'} · {o.days_old}д
                {o.delivery_date && ` · доставка ${o.delivery_date}`}
                {o.promised_payment_date && ` · обещал ${o.promised_payment_date}`}
              </div>
              <button onClick={() => pay(o.id, o.order_number)}
                className="mt-2 w-full text-xs text-green-700 border border-green-300 rounded py-1.5 flex items-center justify-center gap-1"
                data-testid={`finapp-drill-pay-${o.order_number}`}>
                <CheckCircle className="w-3.5 h-3.5" /> Оплачено
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Header + accordion of clients
  return (
    <div className="space-y-2" data-testid="finapp-clients-aging">
      {/* Summary row */}
      <div className="bg-white rounded-lg border p-2 flex items-center gap-2 text-[11px]">
        <div className="flex-1">
          <div className="text-gray-500">Клиентов с долгом</div>
          <div className="font-bold text-sm">{data.total_clients}</div>
        </div>
        <div className="flex-1 text-right">
          <div className="text-gray-500">Общий остаток</div>
          <div className="font-bold text-sm text-emerald-800">{fmt(data.total_balance != null ? data.total_balance : data.total_owed)}</div>
        </div>
      </div>
      {/* Role filter */}
      <div className="flex rounded-md border overflow-hidden text-[11px]">
        {[{id:'all',l:'Все'},{id:'B2B',l:'B2B'},{id:'B2C',l:'B2C'}].map(o => (
          <button key={o.id} onClick={() => setRole(o.id)}
            className={`flex-1 py-1.5 ${role === o.id ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'}`}
            data-testid={`finapp-clients-role-${o.id}`}>{o.l}</button>
        ))}
      </div>
      {/* Accordion */}
      {bucketed.map(b => {
        const total = b.clients.reduce((s, c) => s + bal(c), 0);
        const open = expanded.has(b.id);
        return (
          <div key={b.id} className="bg-white rounded-lg border overflow-hidden" data-testid={`finapp-bucket-${b.id}`}>
            <button onClick={() => toggle(b.id)}
              className="w-full flex items-center justify-between px-3 py-2 text-left"
              data-testid={`finapp-bucket-hdr-${b.id}`}>
              <div className="flex items-center gap-2">
                {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <div>
                  <div className="text-sm font-semibold">{b.label}</div>
                  <div className="text-[10px] text-gray-500">{b.sub}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-gray-500">{b.clients.length} {b.clients.length === 1 ? 'клиент' : 'клиентов'}</div>
                <div className="text-sm font-bold">{fmt(total)}</div>
              </div>
            </button>
            {open && (
              <div className="border-t p-2 space-y-2 bg-gray-50/50">
                {b.clients.length === 0 && <div className="text-center text-xs text-gray-400 py-3">пусто</div>}
                {b.clients.map(c => (
                  <div key={c.user_id || c.customer_name}
                    className="bg-white border rounded-md p-2.5"
                    data-testid={`finapp-client-${b.id}-${c.user_id || 'guest'}`}>
                    <button onClick={() => setDrill(c)}
                      className="w-full text-left"
                      data-testid={`finapp-client-open-${b.id}-${c.user_id || 'guest'}`}>
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{c.customer_name}</div>
                          <div className="text-[10px] text-gray-500 truncate">
                            {c.customer_type} · {c.orders_count} заказов · старейший {c.oldest_days}д
                          </div>
                          {c.paid_ledger_total > 0 && (
                            <div className="text-[10px] text-blue-600 truncate">оплачено {fmt(c.paid_ledger_total)} из {fmt(c.total_owed)}</div>
                          )}
                        </div>
                        <div className={`text-sm font-bold whitespace-nowrap ${bal(c) <= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(bal(c))}</div>
                      </div>
                    </button>
                    <div className="grid grid-cols-2 gap-1.5 mt-2">
                      <button onClick={() => openClientReminder(c, 'whatsapp')}
                        disabled={!c.customer_phone}
                        className="text-[11px] text-green-700 border border-green-300 rounded py-1 flex items-center justify-center gap-1 disabled:opacity-40"
                        data-testid={`finapp-client-wa-${c.user_id || 'guest'}`}>
                        <MessageCircle className="w-3 h-3" /> WhatsApp
                      </button>
                      <button onClick={() => openClientReminder(c, 'telegram')}
                        className="text-[11px] text-blue-700 border border-blue-300 rounded py-1 flex items-center justify-center gap-1"
                        data-testid={`finapp-client-tg-${c.user_id || 'guest'}`}>
                        <Send className="w-3 h-3" /> Telegram
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ExpensesTab({ client }) {
  const [d, setD] = useState(null);
  const [cats, setCats] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ category: 'Аренда', amount: '', occurred_on: new Date().toISOString().slice(0, 10), counterparty: '', note: '' });
  const load = useCallback(() => { client.get('/finance-app/expenses').then(r => setD(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); client.get('/finance-app/expense-categories').then(r => setCats(r.data.categories)).catch(() => {}); /* eslint-disable-next-line */ }, [load]);
  const save = async () => {
    if (!form.amount) { toast.error('Укажите сумму'); return; }
    try { await client.post('/finance-app/expenses', form); toast.success('Добавлено'); setShow(false); setForm({ ...form, amount: '', counterparty: '', note: '' }); load(); }
    catch { toast.error('Ошибка'); }
  };
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-emerald-600 mx-auto mt-8" />;
  return (
    <div data-testid="finapp-expenses">
      <div className="bg-white rounded-xl p-3 border mb-3 flex items-center justify-between">
        <div><div className="text-[11px] text-gray-500">Расходы (тек. месяц)</div><div className="text-lg font-bold">{fmt(d.summary.total)}</div></div>
        <button onClick={() => setShow(true)} className="bg-emerald-600 text-white text-sm rounded-lg px-3 py-2 flex items-center gap-1" data-testid="finapp-add-expense-btn"><Plus className="w-4 h-4" /> Добавить</button>
      </div>
      <div className="space-y-2">
        {d.expenses.slice(0, 50).map((e, i) => (
          <div key={e.id} className="bg-white rounded-lg border p-3" data-testid={`finapp-expense-${i}`}>
            <div className="flex justify-between"><span className="text-sm font-medium">{e.category}</span><span className="font-bold text-sm">{fmt(e.amount)}</span></div>
            <div className="text-[11px] text-gray-500">{e.occurred_on} · {e.counterparty || '—'} · {e.source === 'inventory_purchase' ? 'из склада' : 'вручную'}</div>
          </div>
        ))}
        {d.expenses.length === 0 && <div className="text-center text-xs text-gray-400 py-4">Нет расходов</div>}
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/40 flex items-end z-30" onClick={() => setShow(false)}>
          <div className="bg-white w-full rounded-t-2xl p-4 space-y-3" onClick={e => e.stopPropagation()} data-testid="finapp-expense-form">
            <div className="font-semibold">Новый расход</div>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border rounded-lg p-2 text-sm" data-testid="finapp-exp-category">
              {cats.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" placeholder="Сумма ₸" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="w-full border rounded-lg p-2 text-sm" data-testid="finapp-exp-amount" />
            <input type="date" value={form.occurred_on} onChange={e => setForm({ ...form, occurred_on: e.target.value })} className="w-full border rounded-lg p-2 text-sm" />
            <input placeholder="Контрагент" value={form.counterparty} onChange={e => setForm({ ...form, counterparty: e.target.value })} className="w-full border rounded-lg p-2 text-sm" />
            <input placeholder="Заметка" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className="w-full border rounded-lg p-2 text-sm" />
            <button onClick={save} className="w-full bg-emerald-600 text-white rounded-lg py-2.5 font-medium" data-testid="finapp-exp-save">Сохранить</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PnlTab({ client }) {
  const [d, setD] = useState(null);
  useEffect(() => { client.get('/finance-app/pnl').then(r => setD(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-emerald-600 mx-auto mt-8" />;
  return (
    <div data-testid="finapp-pnl">
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Stat icon={TrendingUp} tint="emerald" label="Выручка" value={fmt(d.totals.revenue)} />
        <Stat icon={TrendingDown} tint="red" label="Расходы" value={fmt(d.totals.expenses)} />
        <Stat icon={Wallet} tint={d.totals.profit >= 0 ? 'emerald' : 'red'} label="Прибыль" value={fmt(d.totals.profit)} />
        <Stat icon={Percent} tint="blue" label="Маржа" value={`${d.totals.margin}%`} />
      </div>
      <div className="text-xs font-semibold text-gray-600 mb-1 px-1">По филиалам</div>
      <div className="space-y-2">
        {d.by_branch.map((b, i) => (
          <div key={b.branch_id || i} className="bg-white rounded-lg border p-3" data-testid={`finapp-branch-${i}`}>
            <div className="flex justify-between"><span className="font-medium text-sm">{b.branch_name}</span><span className={`font-bold text-sm ${b.profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(b.profit)}</span></div>
            <div className="text-[11px] text-gray-500">Выручка {fmt(b.revenue)} · Расходы {fmt(b.expenses)} · Маржа {b.margin}%</div>
          </div>
        ))}
        {d.by_branch.length === 0 && <div className="text-center text-xs text-gray-400 py-4">Нет данных</div>}
      </div>
    </div>
  );
}

export default FinanceApp;
